import {
    createContext,
    useContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import type { FormDefinition, Section } from "@/lib/api";
import { saveAnswers } from "@/lib/api";
import { AutoSaveEngine } from "@/lib/autosave";
import { Recorder } from "@/lib/recorder";
import { computeVisitState } from "@/lib/visit-state";

interface FormContextValue {
    form: FormDefinition;
    /** Current answers: questionId → value */
    answers: Record<string, string>;
    /** Update an answer locally + mark dirty for auto-save */
    setAnswer: (questionId: string, value: string) => void;
    /** Flush pending saves immediately */
    flushSaves: () => Promise<boolean>;
    /** Auto-save status */
    saveStatus: "idle" | "saving" | "error" | "saved";
    /** Get a section by ID */
    getSection: (sectionId: string) => Section | undefined;
    /** Whether the form is read-only: the visit has not been clocked into yet,
     *  or it is finished (completed/sealed). */
    readOnly: boolean;
    /** Reload form data (for status updates after section complete) */
    updateForm: (updater: (prev: FormDefinition) => FormDefinition) => void;
}

/**
 * Question types whose answer can only change by a discrete tap. These commit
 * to the server immediately; everything else (text, number, phone, address,
 * blood pressure, body metrics) rides the 3s debounce, because those are typed
 * and a per-keystroke save would be a request per character.
 *
 * Signatures are deliberately NOT here. `SignatureQuestion` looks like a tap
 * answer but its "unable to sign → Other" branch writes a free-text reason on
 * every keystroke, which would make it the one type that saves per character.
 * It stays debounced; the unmount flush already covers navigating away.
 */
const TAP_ANSWER_TYPES = new Set(["radio", "check", "yesNo", "dropDown", "date", "time"]);

const Context = createContext<FormContextValue | null>(null);

export function useFormContext() {
    const ctx = useContext(Context);
    if (!ctx) {
        throw new Error("useFormContext must be used within FormProvider");
    }
    return ctx;
}

interface FormProviderProps {
    form: FormDefinition;
    onFormChange: (form: FormDefinition) => void;
    children: React.ReactNode;
}

export function FormProvider({ form, onFormChange, children }: FormProviderProps) {
    const [answers, setAnswers] = useState<Record<string, string>>(() => {
        // Initialize from existing answers loaded from server
        const map: Record<string, string> = {};
        for (const a of form.answers) {
            map[a.questionId] = a.value;
        }
        // Resolve patient data keys for locked questions (e.g. gender → patient.gender)
        if (form.patient) {
            for (const phase of form.phases) {
                for (const section of phase.sections) {
                    for (const q of section.questions) {
                        if (q.patientDataKey && !map[q.id]) {
                            const val = form.patient[q.patientDataKey as keyof typeof form.patient];
                            if (typeof val === "string") {
                                map[q.id] = val;
                            }
                        }
                    }
                }
            }
        }
        return map;
    });

    // Sync answers when form data is reloaded from server (e.g. after dev-auto-fill)
    useEffect(() => {
        const serverAnswers: Record<string, string> = {};
        for (const a of form.answers) {
            serverAnswers[a.questionId] = a.value;
        }
        setAnswers((prev) => ({ ...prev, ...serverAnswers }));
    }, [form.answers]);

    const [saveStatus, setSaveStatus] = useState<FormContextValue["saveStatus"]>("idle");

    // Bind the autosave target to THIS provider's visit. Combined with the
    // per-visit `key` on FormProvider (see visit/[id]/_layout.tsx), the engine is
    // created once per visit and can only ever write to the visit it was created
    // for. Previously saveFn read a mutable ref that got repointed when Expo
    // Router reused the screen instance for another visit, so a stale dirty buffer
    // flushed one visit's answers into another (incident 2026-07-06). A captured
    // const cannot be repointed.
    const boundVisitId = form.visit.id;

    // The nurse just changed an assessment answer, and the server dropped the
    // plan of care and the supervisory built on it. `sectionStatuses` is loaded
    // once with `/form` and only ever updated locally — nothing refetches it on
    // focus — so without this the two steps keep their ✓ for the rest of the
    // session: Plan of Care reads as done with no plan behind it, Supervisory
    // stays unlocked, and rating it there pulls the generic fallback duty list.
    //
    // Held in a ref because the autosave engine is constructed once and must not
    // capture a stale `form`.
    const dropDerivedSectionStatuses = useCallback(() => {
        const derivedSectionIds = new Set(
            form.phases
                .flatMap((phase) => phase.sections)
                .filter(
                    (section) =>
                        section.behaviorType === "plan_of_care" ||
                        section.behaviorType === "supervisory_evaluation"
                )
                .map((section) => String(section.id))
        );
        if (derivedSectionIds.size === 0) {
            return;
        }
        onFormChange({
            ...form,
            sectionStatuses: form.sectionStatuses.map((status) =>
                derivedSectionIds.has(String(status.sectionId))
                    ? { ...status, status: "not_started" as const, completedAt: null }
                    : status
            )
        });
    }, [form, onFormChange]);

    const dropDerivedSectionStatusesRef = useRef(dropDerivedSectionStatuses);
    dropDerivedSectionStatusesRef.current = dropDerivedSectionStatuses;

    const engineRef = useRef<AutoSaveEngine | null>(null);
    if (!engineRef.current) {
        engineRef.current = new AutoSaveEngine({
            debounceMs: 3000,
            saveFn: async (pending) => {
                try {
                    const res = await saveAnswers(
                        boundVisitId,
                        pending.map((p) => ({ questionId: p.questionId, value: p.value }))
                    );
                    if (res.derivedInvalidated) {
                        dropDerivedSectionStatusesRef.current();
                    }
                    Recorder.addEvent("form_save", {
                        visitId: boundVisitId,
                        questionIds: pending.map((p) => p.questionId)
                    });
                    return true;
                } catch (err) {
                    Recorder.addEvent(
                        "form_save_failed",
                        {
                            visitId: boundVisitId,
                            questionIds: pending.map((p) => p.questionId),
                            message: err instanceof Error ? err.message : String(err)
                        },
                        "error"
                    );
                    return false;
                }
            },
            onStatusChange: setSaveStatus
        });
    }

    // On unmount — including the per-visit remount that fires when the RN
    // navigates to another visit — flush any still-dirty answers to THIS visit
    // before tearing the engine down, so the last keystrokes aren't lost. The
    // flush targets `boundVisitId`, so it always lands on the correct visit.
    useEffect(() => {
        return () => {
            void engineRef.current?.flush();
            engineRef.current?.destroy();
        };
    }, []);

    // questionId → type, so `setAnswer` can pick the commit path without every
    // call site having to declare it. Held in a ref because `setAnswer` is a
    // stable callback and must not capture a stale form.
    const questionTypes = useMemo(() => {
        const map = new Map<string, string>();
        for (const phase of form.phases) {
            for (const section of phase.sections) {
                for (const q of section.questions) {
                    map.set(String(q.id), q.type);
                }
            }
        }
        return map;
    }, [form.phases]);
    const questionTypesRef = useRef(questionTypes);
    questionTypesRef.current = questionTypes;

    const setAnswer = useCallback((questionId: string, value: string) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
        const type = questionTypesRef.current.get(String(questionId));
        if (type && TAP_ANSWER_TYPES.has(type)) {
            // Fire-and-forget: the engine re-queues on failure, and blocking a
            // radio tap on the network would make the form feel broken offline.
            void engineRef.current?.markDirtyNow(questionId, value);
        } else {
            engineRef.current?.markDirty(questionId, value);
        }
    }, []);

    const flushSaves = useCallback(async () => {
        return engineRef.current?.flush() ?? Promise.resolve(true);
    }, []);

    const getSection = useCallback(
        (sectionId: string): Section | undefined => {
            for (const phase of form.phases) {
                const section = phase.sections.find((s) => String(s.id) === sectionId);
                if (section) {
                    return section;
                }
            }
            return undefined;
        },
        [form]
    );

    // ONE general write-guard for the whole visit, at the only place every
    // screen already reads from. It is deliberately state-based, never keyed on
    // a phase or section name, so a new phase is guarded the day it is added.
    //
    // State 1 = Pre-Visit (assigned, never clocked in). The RN may browse the
    // entire form but may not type into any of it. Until now only assessment.tsx
    // enforced that, via its own local `isAssessmentReadOnly` call — so the
    // Welcome Package, where the patient signs the Bill of Rights, HIPAA consent
    // and financial responsibility, accepted signatures before the nurse had
    // even arrived. `computeVisitState` already encodes the exceptions
    // correctly: a missed visit is terminal (4), and a visit reopened for
    // resubmission is active (2), not pre-visit.
    //
    // The completed/sealed arms are kept verbatim rather than folded into
    // `isAssessmentReadOnly`, because that helper treats "submitted but not yet
    // clocked out" (state 3) as editable while this context has always treated
    // `completed` as locked. Swapping it wholesale would silently unlock those.
    const preVisit = computeVisitState(form.visit) === 1;
    const readOnly =
        preVisit ||
        (form.visit.status === "completed" && !form.visit.openForResubmission) ||
        form.visit.status === "sealed";

    const updateForm = useCallback(
        (updater: (prev: FormDefinition) => FormDefinition) => {
            onFormChange(updater(form));
        },
        [form, onFormChange]
    );

    return (
        <Context.Provider
            value={{
                form,
                answers,
                setAnswer,
                flushSaves,
                saveStatus,
                getSection,
                readOnly,
                updateForm
            }}
        >
            {children}
        </Context.Provider>
    );
}
